/* eslint no-console: "off" */
import { useCallback, useState, useMemo, ReactNode, useEffect, useRef } from 'react';
import { changeLanguage } from 'i18next';

import { loaders, LoggedUser, LoginSuccess, actions } from 'entities';

import { useToast } from 'providers/Toast';
import { useRealTime } from 'providers/RealTime';

import { getSavedUserInfo, validateSession } from './utils';
import { SECURITY_LOGOUT_MESSAGE } from 'utils/constants';

import { ApiError } from 'types/api';

import { AuthenticatedUser, tLoginAction } from './types';
import { AuthContext } from './auth.context';
import { authStorage } from './storage';

export function AuthProvider({ children }: { children: ReactNode }) {
  const { addToast } = useToast();

  const { setUserId } = useRealTime();

  const [userData, setUserData] = useState<AuthenticatedUser>(getSavedUserInfo);

  const [loginAction, setLoginAction] = useState<tLoginAction>(() => {
    if (userData.token) return null;

    return 'login';
  });

  const signOut = useCallback(
    async ({ errorMessage }: { errorMessage?: string } = {}) => {
      if (errorMessage) addToast({ title: errorMessage });

      setUserId(null);
      authStorage.user.delete();

      setLoginAction('login');
      setUserData({ data: {} } as AuthenticatedUser);
    },
    [addToast, setUserId],
  );

  const logoutTimerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (loginAction) return;

    logoutTimerRef.current = setInterval(
      () => {
        const validSession = validateSession(userData);

        if (validSession) return;

        signOut({
          errorMessage: 'Session expired',
        });
      },
      5 * 1000 * 60,
    );

    return () => clearInterval(logoutTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginAction, userData]);

  const updateUserMainData = useCallback((user: Partial<LoggedUser>) => {
    setUserData((old) => {
      const updatedInfo = {
        ...old,
        data: {
          ...old.data,
          ...user,
        },
      };

      authStorage.user.set(updatedInfo);

      return updatedInfo;
    });
  }, []);

  loaders.users.find.use({
    params: { id: `${userData?.data.id}` },

    blockLoad: !!loginAction || !userData?.data.id,

    onError: ({ response }) => {
      if ((response?.data as ApiError).message === SECURITY_LOGOUT_MESSAGE)
        signOut({ errorMessage: 'Security updated executed. Sign required' });
    },

    refreshMinutes: 5,
  });

  const updateUserPicture = useCallback(
    (picture: string) => {
      updateUserMainData({ picture });
    },
    [updateUserMainData],
  );

  const initiateLogin = actions.auth.initiateLogin.use();

  const requestSignIn = useCallback(
    async (email: string) => {
      const result = await initiateLogin.execute({
        email,
      });

      if (result?.challenge !== 'SLACK_CODE') return false;

      authStorage.currentSessionAttempt.set({ email, sessionId: result.sessionId });

      setLoginAction('code-sent');

      return true;
    },
    [initiateLogin],
  );

  const navigateToCodeInput = useCallback(() => {
    setLoginAction('code-input');
  }, []);

  const navigateToLogin = useCallback(() => {
    setLoginAction('login');
    authStorage.currentSessionAttempt.delete();
  }, []);

  const setLogin = useCallback(
    ({ accessValidity, token, user }: LoginSuccess) => {
      const stateData = {
        token,
        accessValidity,
        data: user,
      };

      changeLanguage(user.language || 'en');

      authStorage.currentSessionAttempt.delete();
      authStorage.user.set(stateData);

      setUserData(stateData);
      setUserId(user.id);

      setLoginAction(null);
    },
    [setUserId],
  );

  const completeLogin = actions.auth.completeLogin.use({
    onSuccess: setLogin,
  });

  const finishLogin = useCallback(
    async (payload: { code: string }) => {
      const session = authStorage.currentSessionAttempt.get();

      if (!session) return false;

      const result = await completeLogin.execute({
        ...payload,
        ...session,
      });

      return !!result;
    },
    [completeLogin],
  );

  const data = useMemo(
    () => ({
      user: userData,
      requestSignIn,
      signOut,
      updateUserPicture,
      navigateToCodeInput,
      loginAction,
      finishLogin,
      navigateToLogin,
      updateUserMainData,
      setLogin,
    }),
    [
      userData,
      loginAction,
      requestSignIn,
      finishLogin,
      navigateToCodeInput,
      signOut,
      updateUserPicture,
      navigateToLogin,
      updateUserMainData,
      setLogin,
    ],
  );

  return <AuthContext.Provider value={data}>{children}</AuthContext.Provider>;
}
